import { ChevronUpIcon } from "@components/Icons";
import ComponentTable from "@components/Tables/ComponentTable";
import { ComponentTableHeader } from "@components/Tables/ComponentTableHeader";
import DocumentTable from "@components/Tables/DocumentTable";
import TableWithPagination from "@components/Tables/TableWithPagination";
import TabsContainer from "@components/Tabs/TabsContainer";
import { BasicInfoSection, Breadcrumb, NoticeInformationViewModal, WorkflowProgressNotice } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { useNoticeInformation } from "@hooks/useNoticeInformation";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { NOTICE_STATUS, translateDepartment } from "@utils/common";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { VEX_SNAPSHOT_SOURCE } from "@constants/vex";

// Helper function to map status codes to display names
const mapStatusToDisplayName = (statusCode) => {
  switch (statusCode) {
    case NOTICE_STATUS.REQUEST:
      return "Request Writing";
    case NOTICE_STATUS.DONE:
      return "Write Completed";
    case NOTICE_STATUS.UNNECESSARY:
      return "No Need to Write";
    default:
      return statusCode || "-";
  }
};

const TEST_ID = "notice-information-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const NoticeInformationDetail = () => {
  const { noticeInfoSeq } = useParams();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [memberTotalData, setMemberTotalData] = useState(0);
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberItemsPerPage] = useState(5);
  const [memberTotalPages, setMemberTotalPages] = useState(1);
  const [membersData, setMembersData] = useState([]);
  const [documentTotalData, setDocumentTotalData] = useState(0);
  const [fileGroupNo, setFileGroupNo] = useState(null);
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [isProjectInfoCollapsed, setIsProjectInfoCollapsed] = useState(false);
  const [isReportInfoCollapsed, setIsReportInfoCollapsed] = useState(false);
  const [isOssUsedInfoCollapsed, setIsOssUsedInfoCollapsed] = useState(false);
  const [showNoticeInfoViewModal, setShowNoticeInfoViewModal] = useState(false);
  // Hooks
  const {
    noticeStatus,
    noticeInfo,
    projectData,
    isLoadingProject,
    isLoadingNoticeInfo,
    isLoadingVerification,
    verificationReportData,
    scanComponents,
    licensePolicyData,
    noticeInfoDownloadByText,
    noticeInfoDownloadByHtml,
    updateNoticeInfoNoticeInfoWriteStatus,
    noticeInfoComponents,
    noticeInfoStatusCdName,
    noticeInfoWriteStatusCd,
    handleMoveToReview,
    handleRejectReview,
    handleMoveToDone,
    isLoading
  } = useNoticeInformation(noticeInfoSeq);

  // API call for getting members by project ID
  const getMembersByProjectId = useFetchData(
    [queryKeys.getMembersByProjectId, projectData?.projectSeq, memberCurrentPage, memberItemsPerPage],
    queryKeys.getMembersByProjectId,
    {
      method: "GET",
    },
    {
      projectSeq: projectData.projectSeq,
      pageSize: memberItemsPerPage,
      startRow: memberItemsPerPage * (memberCurrentPage - 1),
    },
    {
      enabled: !!projectData.projectSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  // Create a custom mutation for getting file information with dynamic URL and pagination (fileGroupNo as query param)
  const getFileInfoMutation = useMutationData(
    ({ fileGroupNo, page = 1, pageSize = 5 }) => {
      const startRow = (page - 1) * pageSize;
      return `${queryKeys.getFileInfo}?fileGroupNo=${fileGroupNo}&pageSize=${pageSize}&startRow=${startRow}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response.object)) {
          setFileInfoModified(response.object);
          setDocumentTotalData(response.objectCount || response.object.length);
        } else if (Array.isArray(response)) {
          setFileInfoModified(response);
          setDocumentTotalData(response.length);
        } else {
          setFileInfoModified([]);
          setDocumentTotalData(0);
        }
      },
      onError: (error) => {
        console.error("Error during get file info:", error);
        setFileInfoModified([]);
        setDocumentTotalData(0);
      },
    },
  );

  // Handle members data response
  useEffect(() => {
    if (getMembersByProjectId.data && !getMembersByProjectId.isLoading && !getMembersByProjectId.error) {
      const response = getMembersByProjectId.data;
      if (typeof response !== "string" && response && response.object && Array.isArray(response.object)) {
        const mappedMembers = response.object.map((member) => ({
          userId: member.member?.userId || member.memberId,
          userName: member.member?.userName || member.memberId,
          deptCdName: member.member?.deptCdName || member.member?.companyCdName || "",
          email: member.member?.email || "",
          roleCdName: member.member?.roleCdName || "",
          companyCdName: member.member?.companyCdName || "",
          position: member.member?.position || "",
          phoneNo: member.member?.phoneNo || "",
          cellNo: member.member?.cellNo || "",
          createdAt: member.createdAt || "",
          createrName: member.createrName || "",
        }));
        setMembersData(mappedMembers);
        setMemberTotalData(response.objectCount || 0);
        setMemberTotalPages(Math.ceil((response.objectCount || 0) / memberItemsPerPage));
      } else {
        // Handle case where response.object is undefined, null, or not an array
        setMembersData([]);
        setMemberTotalData(0);
        setMemberTotalPages(1);
      }
    }
  }, [getMembersByProjectId.data, getMembersByProjectId.isLoading, getMembersByProjectId.error]);

  const handleDownloadDocument = (document) => {
    // For now, we'll just log the download since we need to implement the actual download API
    // TODO: Implement actual download functionality using the fileGroupAPI
  };

  // Status color logic (match ProjectList)
  const renderVerificationStatusCell = (status) => {
    const STATUS = {
      "DISTRIBUTION_COMPLETED": "Distribution Completed",
      "WRITING": "Writing...",
      "REVIEWING": "Reviewing..."
    }

    const statusStyles = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.textSuccess,
      [STATUS.WRITING]: colors.textStatusAnalysis,
      [STATUS.REVIEWING]: colors.textStatusWritingReview,
    };
    const statusColorDot = {
      [STATUS.DISTRIBUTION_COMPLETED]: colors.bgStatusSuccess,
      [STATUS.WRITING]: colors.bgStatusAnalysis,
      [STATUS.REVIEWING]: colors.bgStatusWritingReview,
    };

    if (status) {
      return (
        <span
          className={`inline-flex items-center rounded-full py-0.5 text-sm font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
          data-testid={tid("field-verif-report-status-value")}
        >
          <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`} data-testid={tid("field-verif-report-status-dot")}></div>
          {t(`project.verification-report.verification-report-status-${status?.toLowerCase() || ''}`, status)}
        </span>
      )
    }

    return null
  };

  const projectInfoLoading = isLoadingProject || !projectData;
  const reportInfoLoading = isLoadingVerification || !verificationReportData;

  const renderInfoField = (fieldName, label, value, { title, isLoadingField = false } = {}) => {
    const displayTitle = title ?? value ?? "-";
    return (
      <div data-testid={fieldContainerTestId(fieldName)}>
        <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId(fieldName)}>
          {label}
        </label>
        <p
          title={isLoadingField ? undefined : String(displayTitle)}
          className={`text-sm ${colors.textPrimary} h-8 content-center truncate ${isLoadingField ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
          data-testid={fieldValueTestId(fieldName)}
        >
          {isLoadingField ? "" : (value ?? "-")}
        </p>
      </div>
    );
  };

  // Tab configurations
  const projectInfoTabs = [
    {
      label: t("project.general-information"),
      content: (
        <div className="relative" data-testid={tid("project-general-tab")}>
          <div className="flex items-center justify-between pb-4" data-testid={tid("project-general-header")}>
            <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid("project-general-title")}>
              {t("project.general-information")}
            </h3>
          </div>

          <div className="space-y-2 px-4" data-testid={tid("project-general-grid")}>
            <div className="grid grid-cols-5 gap-x-4 gap-y-4 pt-3" data-testid={tid("project-general-row-1")}>
              {renderInfoField("project-id", t("project.project-id"), projectData?.projectId, { title: projectData?.projectId, isLoadingField: projectInfoLoading })}
              {renderInfoField("project-name", t("project.project-name"), projectData?.projectName, { title: projectData?.projectName, isLoadingField: projectInfoLoading })}
              {renderInfoField(
                "product-name",
                t("project.product-name"),
                projectData?.product?.productName || projectData?.productName || "---",
                { title: projectData?.product?.productName || projectData?.productName || "---", isLoadingField: projectInfoLoading },
              )}
              {renderInfoField("asset-classification", t("project.asset-classification"), projectData?.assetClassificationName, { title: projectData?.assetClassificationName, isLoadingField: projectInfoLoading })}
              {renderInfoField(
                "creation-date",
                t("project.creation-date"),
                projectData?.createdAt ? formatDateTime(projectData.createdAt) : "",
                { title: formatDateTime(projectData?.createdAt), isLoadingField: projectInfoLoading },
              )}
            </div>

            <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} border-dashed pt-3`} data-testid={tid("project-general-row-2")}>
              {renderInfoField(
                "completion-request-date",
                t("project.completion-request-date"),
                projectData?.completionRequestDt ? formatDate(projectData.completionRequestDt) : "",
                { title: formatDate(projectData?.completionRequestDt), isLoadingField: projectInfoLoading },
              )}
              {renderInfoField(
                "creator",
                t("project.creator"),
                <UserNameWithId name={projectData?.createrName} id={projectData?.createrId} layout="inline" />,
                {
                  title: formatUserNameWithId(projectData?.createrName, projectData?.createrId),
                  isLoadingField: projectInfoLoading,
                },
              )}
              {/* {renderInfoField(
                "creator-department",
                t("project.creator-department"),
                translateDepartment(t, projectData?.createrDeptCdName),
                { title: translateDepartment(t, projectData?.createrDeptCdName), isLoadingField: projectInfoLoading },
              )} */}
              {renderInfoField("distribution-scope", t("project.distribution-scope"), projectData?.distributionScopeCdName, { title: projectData?.distributionScopeCdName, isLoadingField: projectInfoLoading })}
              {renderInfoField(
                "last-edited",
                t("common.last-modified-by"),
                <UserNameWithId name={projectData?.updaterName} id={projectData?.updaterId} layout="inline" />,
                {
                  title: formatUserNameWithId(projectData?.updaterName, projectData?.updaterId),
                  isLoadingField: projectInfoLoading,
                },
              )}
              {renderInfoField(
                "last-modified",
                t("common.last-modified-date"),
                projectData?.updatedAt ? formatDateTime(projectData.updatedAt) : "",
                { title: formatDateTime(projectData?.updatedAt), isLoadingField: projectInfoLoading },
              )}
            </div>

            <div className="grid grid-cols-5 gap-x-4 gap-y-4 pt-3" data-testid={tid("project-general-row-3")}>

              {renderInfoField(
                "license",
                t("project.license"),
                projectData?.licenseName || projectData?.licenseClassCdName,
                { title: projectData?.licenseName || projectData?.licenseClassCdName, isLoadingField: projectInfoLoading },
              )}
              {renderInfoField("license-policy", t("project.license-policy"), licensePolicyData?.policyName, { title: licensePolicyData?.policyName, isLoadingField: projectInfoLoading || !licensePolicyData })}
              {renderInfoField("remark", t("project.remark"), projectData?.remark, { title: projectData?.remark, isLoadingField: projectInfoLoading })}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: `${t("project.members")} (${memberTotalData})`,
      content: (() => {
        // const columns = ["userId", "userName", "roleCdName", "deptCdName", "email"];
        const columns = ["userId", "userName", "roleCdName", "email"];
        const columnConfig = {
          userId: {
            headerRender: () => t("project.member.user-id"),
          },
          userName: {
            headerRender: () => t("project.member.user-name"),
          },
          roleCdName: {
            headerRender: () => t("project.member.role"),
          },
          // deptCdName: {
          //   headerRender: () => t("project.member.department"),
          //   customRender: (value) => translateDepartment(t, value),
          // },
          email: {
            headerRender: () => t("project.member.email"),
          },
        };

        return (
          <div data-testid={tid("members-tab")}>
            <div className="flex items-center justify-between pb-4" data-testid={tid("members-header")}>
              <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid("members-title")}>
                {t("project.members")} ({memberTotalData})
              </h3>
              <div className="flex space-x-3" data-testid={tid("members-actions")}></div>
            </div>

            <div data-testid={tid("members-table-wrapper")}>
              <TableWithPagination
                columns={columns}
                columnConfig={columnConfig}
                data={membersData}
                onRowClick={() => { }}
                loading={getMembersByProjectId.isLoading}
                totalPages={memberTotalPages}
                totalData={memberTotalData}
                currentPage={memberCurrentPage}
                setCurrentPage={setMemberCurrentPage}
                maxRowsPerPage={memberItemsPerPage}
                disabledRow={[]}
                hasCheckbox={false}
                hasDeleteColumn={false}
                hasRadioButton={false}
                rowKey="userId"
                shouldShowGoToPage={true}
                dataTestId={tid("members-table")}
              />
            </div>
          </div>
        );
      })(),
    },
    {
      label: `${t("project.document-info")} (${documentTotalData || 0})`,
      content: (() => {
        const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
        const [documentItemsPerPage] = useState(5);
        const documentTotalPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

        // API call for project details
        const queryKey = [queryKeys.getProjectDetail, projectData?.projectSeq];
        const queryUrl = queryKeys.getProjectDetail + "/" + projectData?.projectSeq;

        const getData = useFetchData(
          queryKey,
          queryUrl,
          {
            method: "GET",
          },
          {},
          {
            enabled: !!projectData?.projectSeq,
            retry: false,
            refetchOnWindowFocus: false,
          },
        );

        // Process project data when it's available
        useEffect(() => {
          if (getData.data && !getData.isLoading && !getData.error) {
            const response = getData.data;
            if (typeof response !== "string") {
              if (response.fileGroupNo) {
                setFileGroupNo(response.fileGroupNo);
              }
            }
          }
        }, [getData.data, getData.isLoading, getData.error]);

        // Fetch paginated data when page or page size changes
        useEffect(() => {
          if (fileGroupNo) {
            getFileInfoMutation.mutate({ fileGroupNo, page: documentCurrentPage, pageSize: documentItemsPerPage });
          }
        }, [fileGroupNo, documentCurrentPage, documentItemsPerPage]);

        // Reset to first page when fileGroupNo changes (not when total data changes)
        useEffect(() => {
          setDocumentCurrentPage(1);
        }, [fileGroupNo]);

        return (
          <div data-testid={tid("documents-tab")}>
            <div className="flex items-center justify-between pb-4" data-testid={tid("documents-header")}>
              <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid("documents-title")}>
                {t("project.document-info")} ({documentTotalData || 0})
              </h3>
            </div>
            {fileInfoModified && fileInfoModified.length > 0 ? (
              <div data-testid={tid("documents-table-wrapper")}>
                <DocumentTable
                  data={fileInfoModified}
                  onDownload={handleDownloadDocument}
                  currentPage={documentCurrentPage}
                  totalPages={documentTotalPages}
                  onPageChange={setDocumentCurrentPage}
                  itemsPerPage={documentItemsPerPage}
                  totalData={documentTotalData}
                  hasDeleteColumn={false}
                />
              </div>
            ) : (
              <div className={`py-8 text-center ${colors.textSecondary}`} data-testid={tid("documents-empty")}>
                <p data-testid={tid("documents-empty-text")}>
                  {getFileInfoMutation.isPending ? t("project.loading-files") : t("project.no-documents-found")}
                </p>
              </div>
            )}
          </div>
        );
      })(),
    },
  ];

  if (isLoading) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid={tid("page")}>
      <div className="pb-5" data-testid={tid("header")}>
        <div className="space-y-3" data-testid={tid("header-content")}>
          <Breadcrumb
            data-testid={tid("breadcrumb")}
            items={[
              { label: t("project.notice-infor.notice-infor-list"), href: "/notice-information" },
              { label: t("navbar.notice-information-details") },
            ]}
          />
          <div data-testid={tid("title")}>
            <h1 className={`text-2xl font-bold ${colors.textPrimary}`} data-testid={tid("title-heading")}>
              {isLoadingNoticeInfo || !noticeInfo ? "Loading..." : noticeInfo.noticeInfoName}
            </h1>
          </div>
        </div>
      </div>

      <div data-testid={tid("basic-info-wrapper")}>
        <BasicInfoSection
          data-testid={tid("basic-info-section")}
          data={{
            ...noticeInfo,
            noticeInfoWriteStatusCdName: mapStatusToDisplayName(noticeInfoWriteStatusCd),
          }}
          colors={colors}
          t={t}
          type="notice"
          onDownloadNoticeText={() => noticeInfoDownloadByText.mutate()}
          onDownloadNoticeHtml={() => noticeInfoDownloadByHtml.mutate()}
          onViewNoticeInfo={() => setShowNoticeInfoViewModal(true)}
        />
      </div>

      <div className={`px-6 py-4 mb-4 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid={tid("workflow-section")}>
        <WorkflowProgressNotice
          dataTestId={tid("workflow")}
          noticeStatus={noticeStatus}
          noticeInfoWriteStatusCd={noticeInfoWriteStatusCd}
          projectManagerId={projectData?.projectManagerId}
          noticeInfo={noticeInfo}
          noticeInfoComponents={noticeInfoComponents}
          scanComponents={scanComponents}
          updateNoticeInfoNoticeInfoWriteStatus={updateNoticeInfoNoticeInfoWriteStatus}
          onRequestReview={handleMoveToReview}
          onRejectReview={handleRejectReview}
          onConfirmNotice={handleMoveToDone}
          onReDistribution={handleMoveToReview}
        />
      </div>

      {/* Project Information Section */}
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("project-info")}>
        <div
          data-testid={tid("project-info-toggle")}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsProjectInfoCollapsed(!isProjectInfoCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsProjectInfoCollapsed(!isProjectInfoCollapsed);
            }
          }}
          aria-label={isProjectInfoCollapsed ? "Expand Project Information" : "Collapse Project Information"}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isProjectInfoCollapsed ? "rotate-180" : ""}`}
            data-testid={tid("project-info-chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("project-info-title")}>
            {t("project.project-information-title")}
          </h2>
        </div>
        {!isProjectInfoCollapsed && <TabsContainer tabs={projectInfoTabs} dataTestId={tid("project-tabs")} />}
      </div>

      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("report-info")}>
        <div
          data-testid={tid("report-info-toggle")}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsReportInfoCollapsed(!isReportInfoCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsReportInfoCollapsed(!isReportInfoCollapsed);
            }
          }}
          aria-label={isReportInfoCollapsed ? "Expand Report Information" : "Collapse Report Information"}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isReportInfoCollapsed ? "rotate-180" : ""}`}
            data-testid={tid("report-info-chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("report-info-title")}>
            {t("project.verification-report.verification-report-information")}
          </h2>
        </div>
        {!isReportInfoCollapsed && (
          <div className="relative" data-testid={tid("report-info-content")}>
            <div className="space-y-2 px-4" data-testid={tid("report-info-grid")}>
              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 ${colors.borderSection} pt-3`} data-testid={tid("report-info-row-1")}>
                {renderInfoField("verification-report-id", t("project.verification-report.verification-report-id"), verificationReportData?.verificationReportId, { title: verificationReportData?.verificationReportId, isLoadingField: reportInfoLoading })}
                {renderInfoField("verification-report-name", t("project.verification-report.verification-report-name"), verificationReportData?.verificationReportName, { title: verificationReportData?.verificationReportName, isLoadingField: reportInfoLoading })}
                {renderInfoField(
                  "verification-tool",
                  t("project.verification-report.verification-tool"),
                  formatVerificationType(verificationReportData?.verificationToolCdName),
                  { title: formatVerificationType(verificationReportData?.verificationToolCdName), isLoadingField: reportInfoLoading },
                )}
                <div data-testid={fieldContainerTestId("verif-report-status")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("verif-report-status")}>
                    {t("project.verification-report.verif-report-status")}
                  </label>
                  <div className="h-8 content-center" data-testid={fieldValueTestId("verif-report-status")}>
                    {reportInfoLoading ? "" : renderVerificationStatusCell(verificationReportData?.verificationReportStatusCdName)}
                  </div>
                </div>
              </div>

              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 border-t ${colors.borderSection} border-dashed`} data-testid={tid("report-info-row-2")}>
                {renderInfoField("verif-report-version", t("project.verification-report.verif-report-version"), verificationReportData?.verificationReportVersion, { title: verificationReportData?.verificationReportVersion, isLoadingField: reportInfoLoading })}
                {/* {renderInfoField(
                  "dev-dept-name",
                  t("project.notice-infor.dev-dept-name"),
                  translateDepartment(t, verificationReportData?.createrDeptCdName),
                  { title: translateDepartment(t, verificationReportData?.createrDeptCdName), isLoadingField: reportInfoLoading },
                )} */}
                {renderInfoField("verif-report-file", t("project.notice-infor.verif-report-file"), verificationReportData?.reportFileName, { title: verificationReportData?.reportFileName, isLoadingField: reportInfoLoading })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("oss-section")}>
        {!isOssUsedInfoCollapsed && (
          <div data-testid={tid("oss-table-wrapper")}>
            <ComponentTable
              data={scanComponents || []}
              licensePolicyData={licensePolicyData}
              className=""
              maxRowsPerPage={5}
              showPagination={true}
              isEditingAllowed={false}
              seq="verificationReportDetailSeq"
              isFossIDDependency={false}
              validationMode="export"
              showShieldFilter
              frozenVexSnapshot={
                noticeInfo?.verificationReportSeq
                  ? {
                      source: VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT,
                      verificationReportSeq: Number(noticeInfo.verificationReportSeq),
                    }
                  : null
              }
            >
              <ComponentTableHeader
                title={t("project.notice-infor.oss-used-info-title")}
                count={scanComponents.length || 0}
                isEditMode={false}
                isEditingAllowed={false}
                colors={colors}
                t={t}
              />
            </ComponentTable>
          </div>
        )}
      </div>

      {/* Notice Information View Modal */}
      <NoticeInformationViewModal
        data-testid={tid("view-modal")}
        isOpen={showNoticeInfoViewModal}
        onClose={() => setShowNoticeInfoViewModal(false)}
        noticeInfoComponents={noticeInfoComponents}
        noticeInfo={noticeInfo}
      />

    </div>
  );
};

export default NoticeInformationDetail;
